import { useState, useRef, ChangeEvent, useEffect, Suspense } from "react";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import { Button, SkeletonText } from "@zapier/design-system";

import { App, AppCategory } from "../types";

import { appsByCategoryAtomFamily_SUSPENDS } from "./appsByCategoryAtomFamily_SUSPENDS";
import { useAppSearchData } from "./useAppSearchData";
import { AppList } from "./AppList";
import { popularApps, zapierApps } from "./recommendedApps";
import styles from "./AppPicker.module.css";

function filterOutSelectedApp(
  apps: App[],
  selectedApp: string | undefined,
  limit: number,
) {
  const filtered = apps.filter((app: App) => app.name !== selectedApp);
  return filtered.slice(0, limit);
}

type Props = {
  category?: AppCategory;
  onSelect: (app: App) => void;
  onClose: () => void;
  selectedApp?: string;
  numOfAppsToShow?: number;
  onRemove?: () => void;
  isDarkMode?: boolean;
  shouldCloseOnClickOutside?: boolean;
  showAgentsNodes: boolean;
};

type ChildProps = {
  numOfAppsToShow: number;
  isDarkMode: boolean;
};

export function AppPickerSuspended({
  category,
  onSelect,
  onClose,
  selectedApp,
  numOfAppsToShow,
  onRemove,
  isDarkMode,
  shouldCloseOnClickOutside = true,
  showAgentsNodes,
}: Props & ChildProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const { apps, onSearch, loading } = useAppSearchData({
    // + 1 'cuz we have extra space when there's a remove app button (it'll be hidden during search)
    limit: onRemove ? numOfAppsToShow + 1 : numOfAppsToShow,
    searchDebounceDelay: 300,
    excludeUpcoming: true,
    includeIsAppAdmin: true,
    includeIsAppCollaborator: true,
    apiHostname: "", // relative queries
    showAgentsNodes,
  });

  const categoryApps = useAtomValue(
    appsByCategoryAtomFamily_SUSPENDS(category),
  );

  const zapierApps = getZapierApps();

  const suggestedApps = getSuggestedApps({
    selectedApp,
    numOfAppsToShow,
    categoryApps,
  });

  // Add click outside handler
  useEffect(() => {
    if (!shouldCloseOnClickOutside) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, shouldCloseOnClickOutside]);

  const onChangeApp = (service: App | null) => {
    if (service) {
      onSelect(service);
    }
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
    onSearch(e.target.value);
  };

  const handleAppClick = (app: App) => {
    onChangeApp(app);
  };

  const renderApps = () => {
    if (searchInputValue) {
      return (
        <AppList
          apps={apps}
          handleAppClick={handleAppClick}
          isLoading={loading}
          numOfAppsToShow={numOfAppsToShow}
          title="Search results"
          isDarkMode={isDarkMode}
        />
      );
    }

    const filteredZapierApps = zapierApps.filter((app) => {
      if (!showAgentsNodes && app.name === "Zapier Agents") return false;
      return true;
    });

    return (
      <div className={styles.appGrid}>
        <AppList
          apps={suggestedApps}
          handleAppClick={handleAppClick}
          title="Suggested apps"
          numOfAppsToShow={numOfAppsToShow}
          isDarkMode={isDarkMode}
        />
        <AppList
          apps={filteredZapierApps}
          handleAppClick={handleAppClick}
          title="Zapier apps"
          numOfAppsToShow={numOfAppsToShow}
          isDarkMode={isDarkMode}
        />
      </div>
    );
  };

  return (
    <div
      className={classNames(styles.appPickerContainer, {
        [styles.appPickerContainerDarkMode]: isDarkMode,
        [styles.appPickerContainerNoRemoveButton]: !onRemove,
      })}
      ref={dropdownRef}
    >
      {/* Search Input */}
      <input
        type="text"
        className={classNames(styles.searchInput, {
          [styles.searchInputDarkMode]: isDarkMode,
        })}
        placeholder="Search apps"
        value={searchInputValue}
        onChange={handleSearchChange}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />

      {/* App Selection Grid */}
      <div className={styles.appWrapper}>
        {renderApps()}
        {onRemove && !searchInputValue && (
          <div
            className={classNames(styles.removeAppButtonWrapper, {
              [styles.removeAppButtonWrapperDarkMode]: isDarkMode,
            })}
          >
            <Button variant="tertiary" size="compact" onPress={onRemove}>
              Remove app
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

type LoaderProps = { numOfAppsToShow: number; isDarkMode: boolean };

function AppPickerLoader({ numOfAppsToShow, isDarkMode }: LoaderProps) {
  return (
    <div
      className={classNames(styles.appPickerContainer, styles.loading, {
        [styles.appPickerContainerDarkMode]: isDarkMode,
      })}
    >
      <SkeletonText
        lines={numOfAppsToShow}
        size="body"
        textAlign="left"
        width="100%"
        color={isDarkMode ? "GrayWarm8" : "GrayWarm2"}
      />
    </div>
  );
}

export function AppPicker(props: Props) {
  const numOfAppsToShow = props.numOfAppsToShow ?? 7;
  const isDarkMode = props.isDarkMode ?? false;

  return (
    <Suspense
      fallback={
        <AppPickerLoader
          numOfAppsToShow={numOfAppsToShow + 4}
          isDarkMode={isDarkMode}
        />
      }
    >
      <AppPickerSuspended
        {...props}
        numOfAppsToShow={numOfAppsToShow}
        isDarkMode={isDarkMode}
      />
    </Suspense>
  );
}

function getZapierApps() {
  return zapierApps.map((app) => {
    const slugsToRename = ["interface", "interfaces"];
    if (slugsToRename.includes(app.slug)) {
      return { ...app, name: "Zapier Forms" };
    }

    return app;
  });
}

function getSuggestedApps({
  selectedApp,
  numOfAppsToShow,
  categoryApps,
}: {
  selectedApp: string | undefined;
  numOfAppsToShow: number;
  categoryApps: App[];
}) {
  const apps = categoryApps?.length
    ? filterOutSelectedApp(categoryApps, selectedApp, numOfAppsToShow)
    : filterOutSelectedApp(popularApps, selectedApp, numOfAppsToShow);

  return apps;
}
